import React, { useState, useEffect } from "react";
import "./4DemoNewCase.css";

const Demo4Page = () => {
  const [selectedCase, setSelectedCase] = useState(0);

  // 여러 케이스의 가상 데이터
  const generateAllCasesData = () => {
    const cases = [];
    for (let caseNum = 0; caseNum < 12; caseNum++) {
      const dataPoints = 100;
      const cbfData = [];
      const ecgData = [];
      const hrData = [];

      for (let i = 0; i < dataPoints; i++) {
        cbfData.push({
          time: i * 0.5, // 0.5초 간격
          value: 85 + Math.random() * 15 + Math.sin(i / 8) * 5,
        });
        ecgData.push({
          time: i * 0.5,
          value: Math.sin(i / 3) * 40 + Math.random() * 10,
        });
        hrData.push({
          time: i * 0.5,
          value: 68 + Math.random() * 12 + Math.sin(i / 10) * 3,
        });
      }

      cases.push({
        id: `CASE-${String(caseNum + 1).padStart(3, "0")}`,
        date: new Date(2025, 7, 21 - caseNum).toLocaleDateString("ko-KR"),
        duration: 300 + Math.floor(Math.random() * 180), // 5-8분
        cbfData,
        ecgData,
        hrData,
        avgCBF: 85 + Math.random() * 10,
        avgHR: 72 + Math.random() * 8,
      });
    }
    return cases;
  };

  const [allCases] = useState(generateAllCasesData());
  const currentCase = allCases[selectedCase];

  // 전체 케이스 통계
  const getOverallStats = () => {
    const totalDuration = allCases.reduce((sum, c) => sum + c.duration, 0);
    const avgDuration = totalDuration / allCases.length;
    const avgCBF =
      allCases.reduce((sum, c) => sum + c.avgCBF, 0) / allCases.length;
    const avgHR =
      allCases.reduce((sum, c) => sum + c.avgHR, 0) / allCases.length;

    return {
      totalCases: allCases.length,
      avgDuration: Math.floor(avgDuration),
      avgCBF: avgCBF.toFixed(1),
      avgHR: Math.floor(avgHR),
    };
  };

  const stats = getOverallStats();

  // 케이스별 산점도 데이터 생성
  const generateScatterDataForCase = (caseItem) => {
    const cbfValues = caseItem.cbfData.map((d) => d.value);
    const scatterPoints = [];

    // CBF 데이터를 여러 구간으로 나누어 AUC, AMP, SLP 계산
    const windowSize = 10; // 10개 데이터 포인트씩 윈도우

    for (let i = 0; i <= cbfValues.length - windowSize; i += 5) {
      const window = cbfValues.slice(i, i + windowSize);

      // AUC (Area Under Curve) - 윈도우 내 곡선 아래 면적
      const auc = window.reduce((sum, value, index) => {
        if (index === 0) return sum;
        return sum + (value + window[index - 1]) * 0.5 * 0.5;
      }, 0);

      // AMP (Amplitude) - 윈도우 내 최대값과 최소값의 차이
      const amp = Math.max(...window) - Math.min(...window);

      // SLP (Slope) - 윈도우 내 기울기
      const n = window.length;
      const sumX = window.reduce((sum, _, idx) => sum + idx, 0);
      const sumY = window.reduce((sum, val) => sum + val, 0);
      const sumXY = window.reduce((sum, val, idx) => sum + idx * val, 0);
      const sumX2 = window.reduce((sum, _, idx) => sum + idx * idx, 0);
      const slp = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

      // 윈도우 평균 CBF
      const avgCBF = window.reduce((sum, val) => sum + val, 0) / window.length;

      scatterPoints.push({
        cbf: avgCBF,
        auc: auc / 100, // 스케일 조정
        amp: amp,
        slp: slp * 5, // 스케일 조정
      });
    }

    return scatterPoints;
  };

  // 전체 케이스 상관관계 데이터 생성 (전체 통계용)
  const generateCorrelationData = () => {
    const correlationData = [];

    allCases.forEach((caseItem, caseIndex) => {
      const cbfValues = caseItem.cbfData.map((d) => d.value);

      // 전체 케이스의 AUC, AMP, SLP 계산
      const auc = cbfValues.reduce((sum, value, index) => {
        if (index === 0) return sum;
        return sum + (value + cbfValues[index - 1]) * 0.5 * 0.5;
      }, 0);

      const amp = Math.max(...cbfValues) - Math.min(...cbfValues);

      const n = cbfValues.length;
      const sumX = cbfValues.reduce((sum, _, i) => sum + i, 0);
      const sumY = cbfValues.reduce((sum, val) => sum + val, 0);
      const sumXY = cbfValues.reduce((sum, val, i) => sum + i * val, 0);
      const sumX2 = cbfValues.reduce((sum, _, i) => sum + i * i, 0);
      const slp = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

      const avgCBF =
        cbfValues.reduce((sum, val) => sum + val, 0) / cbfValues.length;

      correlationData.push({
        caseId: caseItem.id,
        cbf: avgCBF,
        auc: auc / 1000,
        amp: amp,
        slp: slp * 10,
        group: caseIndex < 6 ? "A" : "B",
      });
    });

    return correlationData;
  };

  const correlationData = generateCorrelationData();

  // 산점도 차트 렌더링 함수
  const renderScatterChart = (xData, yData, xLabel, yLabel, height = 300) => {
    if (!xData || !yData || xData.length === 0 || yData.length === 0)
      return null;

    const xMax = Math.max(...xData);
    const xMin = Math.min(...xData);
    const yMax = Math.max(...yData);
    const yMin = Math.min(...yData);

    const xPadding = (xMax - xMin) * 0.1;
    const yPadding = (yMax - yMin) * 0.1;

    const xRange = xMax + xPadding - (xMin - xPadding);
    const yRange = yMax + yPadding - (yMin - yPadding);

    const leftMargin = 80;
    const rightMargin = 40;
    const topMargin = 20;
    const bottomMargin = 60;
    const chartWidth = 400 - leftMargin - rightMargin;
    const chartHeight = height - topMargin - bottomMargin;

    // 상관계수 계산
    const n = xData.length;
    const sumX = xData.reduce((sum, val) => sum + val, 0);
    const sumY = yData.reduce((sum, val) => sum + val, 0);
    const sumXY = xData.reduce((sum, val, i) => sum + val * yData[i], 0);
    const sumX2 = xData.reduce((sum, val) => sum + val * val, 0);
    const sumY2 = yData.reduce((sum, val) => sum + val * val, 0);

    const correlation =
      (n * sumXY - sumX * sumY) /
      Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    return (
      <svg className="scatter-chart" viewBox={`0 0 400 ${height}`}>
        {/* 배경 */}
        <rect
          x={leftMargin}
          y={topMargin}
          width={chartWidth}
          height={chartHeight}
          fill="#fafbfc"
          stroke="#e0e0e0"
          strokeWidth="1"
        />

        {/* 그리드 라인 */}
        {[0, 25, 50, 75, 100].map((percent) => (
          <g key={percent}>
            <line
              x1={leftMargin}
              y1={topMargin + (percent / 100) * chartHeight}
              x2={leftMargin + chartWidth}
              y2={topMargin + (percent / 100) * chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
              strokeOpacity="0.5"
            />
            <line
              x1={leftMargin + (percent / 100) * chartWidth}
              y1={topMargin}
              x2={leftMargin + (percent / 100) * chartWidth}
              y2={topMargin + chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
              strokeOpacity="0.5"
            />
          </g>
        ))}

        {/* Y축 레이블 */}
        {[0, 25, 50, 75, 100].map((percent) => {
          const value = yMax + yPadding - (percent / 100) * yRange;
          return (
            <text
              key={percent}
              x={leftMargin - 10}
              y={topMargin + (percent / 100) * chartHeight}
              fill="#666"
              fontSize="10"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {value.toFixed(1)}
            </text>
          );
        })}

        {/* X축 레이블 */}
        {[0, 25, 50, 75, 100].map((percent) => {
          const value = xMin - xPadding + (percent / 100) * xRange;
          return (
            <text
              key={percent}
              x={leftMargin + (percent / 100) * chartWidth}
              y={topMargin + chartHeight + 15}
              fill="#666"
              fontSize="10"
              textAnchor="middle"
            >
              {value.toFixed(1)}
            </text>
          );
        })}

        {/* 데이터 포인트 */}
        {xData.map((x, i) => {
          const y = yData[i];
          const cx =
            leftMargin + ((x - (xMin - xPadding)) / xRange) * chartWidth;
          const cy = topMargin + ((yMax + yPadding - y) / yRange) * chartHeight;
          // 케이스별 색상 (현재 선택된 케이스에 따라)
          const colors = [
            "#FF6B6B",
            "#4A90E2",
            "#50C878",
            "#FFD700",
            "#9B59B6",
            "#E67E22",
          ];
          const colorIndex = selectedCase % colors.length;
          const color = colors[colorIndex];

          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r="4"
              fill={color}
              opacity="0.8"
              stroke="white"
              strokeWidth="2"
            />
          );
        })}

        {/* 축 라인 */}
        <line
          x1={leftMargin}
          y1={topMargin + chartHeight}
          x2={leftMargin + chartWidth}
          y2={topMargin + chartHeight}
          stroke="#666"
          strokeWidth="2"
        />
        <line
          x1={leftMargin}
          y1={topMargin}
          x2={leftMargin}
          y2={topMargin + chartHeight}
          stroke="#666"
          strokeWidth="2"
        />

        {/* 축 제목 */}
        <text
          x={leftMargin + chartWidth / 2}
          y={height - 10}
          fill="#666"
          fontSize="11"
          textAnchor="middle"
          fontWeight="600"
        >
          {xLabel}
        </text>

        <text
          x="15"
          y={topMargin + chartHeight / 2}
          fill="#666"
          fontSize="11"
          textAnchor="middle"
          fontWeight="600"
          transform={`rotate(-90, 15, ${topMargin + chartHeight / 2})`}
        >
          {yLabel}
        </text>

        {/* 상관계수 표시 */}
        <text
          x={leftMargin + chartWidth - 10}
          y={topMargin + 20}
          fill="#333"
          fontSize="11"
          textAnchor="end"
          fontWeight="600"
        >
          r = {correlation.toFixed(3)}
        </text>
      </svg>
    );
  };

  const renderChart = (data, color, height = 400, showGrid = true) => {
    if (!data || data.length === 0) return null;

    const dataMax = Math.max(...data.map((d) => d.value));
    const dataMin = Math.min(...data.map((d) => d.value));
    const dataRange = dataMax - dataMin;

    // Y축 범위를 데이터 범위보다 10% 더 크게 설정 (패딩 줄임)
    const padding = dataRange * 0.1;
    const maxValue = dataMax + padding;
    const minValue = Math.max(0, dataMin - padding);
    const range = maxValue - minValue;

    // 차트 영역 크기 조정 - 좌측 여백 증가, 상하 여백 최소화
    const leftMargin = 80;
    const rightMargin = 40;
    const topMargin = 20;
    const bottomMargin = 50;
    const chartWidth = 800 - leftMargin - rightMargin;
    const chartHeight = height - topMargin - bottomMargin;

    return (
      <svg className="chart" viewBox={`0 0 800 ${height}`}>
        {/* 배경 */}
        <rect
          x={leftMargin}
          y={topMargin}
          width={chartWidth}
          height={chartHeight}
          fill="#fafbfc"
          stroke="none"
        />

        {/* 수평 그리드 라인 */}
        {showGrid &&
          [0, 20, 40, 60, 80, 100].map((percent) => (
            <line
              key={percent}
              x1={leftMargin}
              y1={topMargin + (percent / 100) * chartHeight}
              x2={leftMargin + chartWidth}
              y2={topMargin + (percent / 100) * chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
              strokeOpacity="0.6"
            />
          ))}

        {/* 수직 그리드 라인 */}
        {showGrid &&
          [0, 20, 40, 60, 80, 100].map((percent) => (
            <line
              key={`v-${percent}`}
              x1={leftMargin + (percent / 100) * chartWidth}
              y1={topMargin}
              x2={leftMargin + (percent / 100) * chartWidth}
              y2={topMargin + chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
              strokeOpacity="0.4"
            />
          ))}

        {/* Y축 레이블 */}
        {showGrid &&
          [0, 20, 40, 60, 80, 100].map((percent) => {
            const value = maxValue - (percent / 100) * range;
            return (
              <text
                key={percent}
                x={leftMargin - 10}
                y={topMargin + (percent / 100) * chartHeight}
                fill="#666"
                fontSize="12"
                textAnchor="end"
                dominantBaseline="middle"
                fontWeight="500"
              >
                {value.toFixed(1)}
              </text>
            );
          })}

        {/* X축 시간 레이블 */}
        {showGrid &&
          [0, 25, 50, 75, 100].map((percent) => {
            const timeIndex = Math.floor((percent / 100) * (data.length - 1));
            const timeValue = data[timeIndex]?.time || 0;
            return (
              <text
                key={`time-${percent}`}
                x={leftMargin + (percent / 100) * chartWidth}
                y={topMargin + chartHeight + 20}
                fill="#666"
                fontSize="11"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {timeValue.toFixed(1)}s
              </text>
            );
          })}

        {/* 데이터 라인 */}
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={data
            .map(
              (d, i) =>
                `${leftMargin + (i / (data.length - 1)) * chartWidth},${
                  topMargin + ((maxValue - d.value) / range) * chartHeight
                }`
            )
            .join(" ")}
        />

        {/* 데이터 포인트 (데이터가 적을 때만 표시) */}
        {data.length <= 50 &&
          data.map((d, i) => (
            <circle
              key={i}
              cx={leftMargin + (i / (data.length - 1)) * chartWidth}
              cy={topMargin + ((maxValue - d.value) / range) * chartHeight}
              r="3"
              fill={color}
              opacity="0.8"
            />
          ))}

        {/* X축 라인 */}
        <line
          x1={leftMargin}
          y1={topMargin + chartHeight}
          x2={leftMargin + chartWidth}
          y2={topMargin + chartHeight}
          stroke="#666"
          strokeWidth="2"
        />

        {/* Y축 라인 */}
        <line
          x1={leftMargin}
          y1={topMargin}
          x2={leftMargin}
          y2={topMargin + chartHeight}
          stroke="#666"
          strokeWidth="2"
        />

        {/* X축 제목 */}
        <text
          x={leftMargin + chartWidth / 2}
          y={height - 10}
          fill="#666"
          fontSize="12"
          textAnchor="middle"
          fontWeight="600"
        >
          시간 (초)
        </text>

        {/* Y축 제목 */}
        <text
          x="20"
          y={topMargin + chartHeight / 2}
          fill="#666"
          fontSize="12"
          textAnchor="middle"
          fontWeight="600"
          transform={`rotate(-90, 20, ${topMargin + chartHeight / 2})`}
        >
          값
        </text>
      </svg>
    );
  };

  return (
    <div className="demo4-container">
      <header className="header">
        <h1>CBF 데이터 분석 대시보드</h1>
        <p className="subtitle">뇌혈류량 데이터 분석 현황</p>
      </header>

      <div className="content">
        {/* 전체 통계 섹션 */}
        <section className="overview-section">
          <div className="overview-card primary">
            <div className="overview-content">
              <h3>총 케이스 수</h3>
              <p className="overview-value">{stats.totalCases}</p>
              <p className="overview-label">건</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-content">
              <h3>평균 측정 시간</h3>
              <p className="overview-value">
                {Math.floor(stats.avgDuration / 60)}:
                {String(stats.avgDuration % 60).padStart(2, "0")}
              </p>
              <p className="overview-label">분:초</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-content">
              <h3>평균 CBF</h3>
              <p className="overview-value">{stats.avgCBF}</p>
              <p className="overview-label">ml/100g/min</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-content">
              <h3>평균 심박수</h3>
              <p className="overview-value">{stats.avgHR}</p>
              <p className="overview-label">bpm</p>
            </div>
          </div>
        </section>

        {/* 케이스 선택 섹션 */}
        <section className="case-selector-section">
          <h2>케이스 선택</h2>
          <div className="case-selector-container">
            <button className="nav-arrow nav-arrow-left">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="case-grid">
              {allCases.slice(0, 6).map((caseItem, index) => (
                <div
                  key={caseItem.id}
                  className={`case-card ${
                    selectedCase === index ? "active" : ""
                  }`}
                  onClick={() => setSelectedCase(index)}
                >
                  <h4>{caseItem.id}</h4>
                  <p className="case-date">{caseItem.date}</p>
                  <p className="case-cbf">CBF: {caseItem.avgCBF.toFixed(1)}</p>
                </div>
              ))}
            </div>
            <button className="nav-arrow nav-arrow-right">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </section>

        {/* 최근 케이스 상세 분석 */}
        <section className="current-case-section">
          <div className="section-header">
            <h2>최근 케이스 분석: {currentCase.id}</h2>
            <span className="case-date">{currentCase.date}</span>
          </div>

          <div className="case-stats">
            <div className="stat-card">
              <h4>측정 시간</h4>
              <p className="stat-value">
                {Math.floor(currentCase.duration / 60)}:
                {String(currentCase.duration % 60).padStart(2, "0")}
              </p>
              <p className="stat-unit">분:초</p>
            </div>
            <div className="stat-card">
              <h4>평균 CBF</h4>
              <p className="stat-value">{currentCase.avgCBF.toFixed(1)}</p>
              <p className="stat-unit"></p>
            </div>
            <div className="stat-card">
              <h4>평균 HR</h4>
              <p className="stat-value">{currentCase.avgHR.toFixed(0)}</p>
              <p className="stat-unit">bpm</p>
            </div>
            <div className="stat-card">
              <h4>데이터 품질</h4>
              <p className="stat-value">96%</p>
              <p className="stat-unit">valid data</p>
            </div>
          </div>

          <div className="charts-section">
            <div className="correlation-charts">
              <div className="scatter-chart-container">
                <h4>AMP vs CBF</h4>
                <div className="chart-wrapper">
                  {(() => {
                    const scatterData = generateScatterDataForCase(currentCase);
                    return renderScatterChart(
                      scatterData.map((d) => d.amp),
                      scatterData.map((d) => d.cbf),
                      "AMP",
                      "CBF",
                      320
                    );
                  })()}
                </div>
              </div>

              <div className="scatter-chart-container">
                <h4>SLP vs CBF</h4>
                <div className="chart-wrapper">
                  {(() => {
                    const scatterData = generateScatterDataForCase(currentCase);
                    return renderScatterChart(
                      scatterData.map((d) => d.slp),
                      scatterData.map((d) => d.cbf),
                      "SLP",
                      "CBF",
                      320
                    );
                  })()}
                </div>
              </div>

              <div className="scatter-chart-container">
                <h4>AUC vs CBF</h4>
                <div className="chart-wrapper">
                  {(() => {
                    const scatterData = generateScatterDataForCase(currentCase);
                    return renderScatterChart(
                      scatterData.map((d) => d.auc),
                      scatterData.map((d) => d.cbf),
                      "AUC",
                      "CBF",
                      320
                    );
                  })()}
                </div>
              </div>
            </div>

            <div className="chart-actions">
              <button className="export-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 10L12 15L17 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 15V3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Export Data
              </button>
            </div>
          </div>
        </section>

        {/* 상세 데이터 테이블 */}
        <section className="table-section">
          <h3>상세 데이터 (최근 10개)</h3>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>시간 (초)</th>
                  <th>CBF</th>
                  <th>HR (bpm)</th>
                  <th>ECG</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {currentCase.cbfData
                  .slice(-10)
                  .reverse()
                  .map((item, index) => (
                    <tr key={index}>
                      <td>{item.time.toFixed(1)}</td>
                      <td>{item.value.toFixed(2)}</td>
                      <td>
                        {currentCase.hrData[
                          currentCase.hrData.length - 10 + index
                        ].value.toFixed(0)}
                      </td>
                      <td>
                        {currentCase.ecgData[
                          currentCase.ecgData.length - 10 + index
                        ].value.toFixed(1)}
                      </td>
                      <td>
                        <span
                          className={`status ${
                            item.value > 85 ? "normal" : "warning"
                          }`}
                        >
                          {item.value > 85 ? "정상" : "주의"}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Demo4Page;
